import {
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { useCallback } from 'react'

import type { GlanceOptions } from './glance'
import { apiClient } from '@/api/client'
import { useMutationWithInvalidation } from '@/hooks/use-mutation-with-invalidation'

// --- Types ---

export type QueueStats = Record<string, number>

export type QueueTrigger = {
  source: string
  correlationId: string
  label?: string
}

export type QueueJob = {
  id: string
  name: string
  data: {
    method: string
    url: string
    data?: unknown
    trigger?: QueueTrigger
  }
  returnvalue: unknown
  failedReason: string | null
  stacktrace: string[]
  attemptsMade: number
  attemptsMax: number | null
  timestamp: number
  processedOn: number | null
  finishedOn: number | null
  delay: number
  status: string
}

export type QueueJobsResponse = {
  jobs: QueueJob[]
  total: number
  status: string
}

// --- API Functions ---

export async function getQueueStats(): Promise<QueueStats> {
  const response = await apiClient.get<QueueStats>('/hubspot/queue/stats')
  return response.data
}

export async function getQueuePaused(): Promise<boolean> {
  const response = await apiClient.get<{ paused: boolean }>(
    '/hubspot/queue/is-paused',
  )
  return response.data.paused
}

export async function getQueueJobs(
  status?: string,
  start = 0,
  limit = 50,
): Promise<QueueJobsResponse> {
  const params: Record<string, string | number> = { start, limit }
  if (status && status !== 'all') params.status = status
  const response = await apiClient.get<QueueJobsResponse>(
    '/hubspot/queue/jobs',
    { params },
  )
  return response.data
}

export async function retryJob(jobId: string): Promise<{ message: string }> {
  const response = await apiClient.post<{ message: string }>(
    `/hubspot/queue/jobs/${jobId}/retry`,
  )
  return response.data
}

export async function removeJob(jobId: string): Promise<{ message: string }> {
  const response = await apiClient.delete<{ message: string }>(
    `/hubspot/queue/jobs/${jobId}`,
  )
  return response.data
}

export async function pauseQueue(): Promise<{ message: string }> {
  const response = await apiClient.post<{ message: string }>(
    '/hubspot/queue/pause',
  )
  return response.data
}

export async function resumeQueue(): Promise<{ message: string }> {
  const response = await apiClient.post<{ message: string }>(
    '/hubspot/queue/resume',
  )
  return response.data
}

export async function cleanQueue(
  status: 'completed' | 'failed',
  grace = 0,
): Promise<{ removed: number }> {
  const response = await apiClient.post<{ removed: number }>(
    '/hubspot/queue/clean',
    { status, grace },
  )
  return response.data
}

// --- Query Keys ---

const QUEUE_KEYS = {
  stats: ['queue', 'stats'] as const,
  paused: ['queue', 'paused'] as const,
  jobs: (status?: string) => ['queue', 'jobs', status ?? 'all'] as const,
  job: (id: string) => ['queue', 'job', id] as const,
}

// --- Hooks ---

/** Every five seconds on the Queue page; a surface that only glances at the
 *  counts passes a slower `refetchInterval`. */
export function useQueueStats(options?: GlanceOptions) {
  return useQuery({
    queryKey: QUEUE_KEYS.stats,
    queryFn: getQueueStats,
    refetchInterval: 5000,
    ...options,
  })
}

export function useQueuePaused() {
  return useQuery({
    queryKey: QUEUE_KEYS.paused,
    queryFn: getQueuePaused,
    refetchInterval: 5000,
  })
}

const INFINITE_PAGE_SIZE = 30

export function useInfiniteQueueJobs(status?: string) {
  return useInfiniteQuery({
    queryKey: [...QUEUE_KEYS.jobs(status), 'infinite'],
    queryFn: ({ pageParam = 0 }) =>
      getQueueJobs(status, pageParam, INFINITE_PAGE_SIZE),
    initialPageParam: 0,
    getNextPageParam: (lastPage, _allPages, lastPageParam) => {
      const nextOffset = (lastPageParam as number) + INFINITE_PAGE_SIZE
      if (nextOffset < lastPage.total) return nextOffset
      return undefined
    },
    refetchInterval: 5000,
  })
}

export function useQueueMutations() {
  const queryClient = useQueryClient()

  const invalidateAll = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['queue'] })
  }, [queryClient])

  const retryMutation = useMutationWithInvalidation(retryJob, invalidateAll)
  const removeMutation = useMutationWithInvalidation(removeJob, invalidateAll)
  const pauseMutation = useMutationWithInvalidation(pauseQueue, invalidateAll)
  const resumeMutation = useMutationWithInvalidation(resumeQueue, invalidateAll)
  const cleanMutation = useMutationWithInvalidation(
    ({ status, grace }: { status: 'completed' | 'failed'; grace?: number }) =>
      cleanQueue(status, grace),
    invalidateAll,
  )

  return {
    retryMutation,
    removeMutation,
    pauseMutation,
    resumeMutation,
    cleanMutation,
  }
}
